import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Box, Typography, Button, CircularProgress, Chip } from '@mui/material';
import MathRenderer from '../components/MathRenderer';
import { supabase } from '../lib/supabase';

function ProblemViewPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [problem, setProblem] = useState(null);
    const [topics, setTopics] = useState([]);
    const [sources, setSources] = useState([]);
    const [techniques, setTechniques] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        async function fetchProblemWithTopics() {
            try {
                // Fetch problem data (removing old techniques join)
                const { data: problemData, error: problemError } = await supabase
                    .from('problems')
                    .select('*')
                    .eq('id', id)
                    .single();

                if (problemError) {
                    console.error('Error fetching problem:', problemError);
                    setIsLoading(false);
                    return;
                }

                setProblem(problemData);

                // Fetch topic names for this problem
                const { data: topicsData, error: topicsError } = await supabase
                    .from('problem_topics')
                    .select(`
                        curriculum_topics (
                            id,
                            name
                        )
                    `)
                    .eq('problem_id', id);

                if (topicsError) {
                    console.error('Error fetching problem topics:', topicsError);
                } else {
                    // Extract topic names from the nested structure
                    const topicNames = topicsData.map(item => item.curriculum_topics);
                    setTopics(topicNames);
                }

                // Fetch source names for this problem
                const { data: sourcesData, error: sourcesError } = await supabase
                    .from('problem_sources')
                    .select(`
                        sources (
                            id,
                            name
                        )
                    `)
                    .eq('problem_id', id);

                if (sourcesError) {
                    console.error('Error fetching problem sources:', sourcesError);
                } else {
                    // Extract source names from the nested structure
                    const sourceNames = sourcesData.map(item => item.sources);
                    setSources(sourceNames);
                }

                // Fetch technique names for this problem
                const { data: techniquesData, error: techniquesError } = await supabase
                    .from('problem_techniques')
                    .select(`
                        techniques (
                            id,
                            name
                        )
                    `)
                    .eq('problem_id', id);

                if (techniquesError) {
                    console.error('Error fetching problem techniques:', techniquesError);
                } else {
                    // Extract technique names from the nested structure
                    const techniqueNames = techniquesData.map(item => item.techniques);
                    setTechniques(techniqueNames);
                }
            } catch (error) {
                console.error('Error fetching data:', error);
            }

            setIsLoading(false);
        }

        fetchProblemWithTopics();
    }, [id]);

    if (isLoading)
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <CircularProgress />
            </Box>
        );

    if (!problem)
        return (
            <Container sx={{ mt: 4 }}>
                <Typography variant="h5" color="error" align="center">
                    Problem not found.
                </Typography>
            </Container>
        );

    return (
        <Container sx={{ mt: 4 }}>
            <Typography variant="h4" gutterBottom align="center">
                Problem Details
            </Typography>

            {problem.difficulty && (
                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
                    <Chip
                        label={`Difficulty: ${problem.difficulty.charAt(0).toUpperCase() + problem.difficulty.slice(1)}`}
                        variant="filled"
                        color="warning"
                        size="large"
                    />
                </Box>
            )}

            {topics.length > 0 && (
                <Box sx={{ mt: 3 }}>
                    <Typography variant="h6" gutterBottom>
                        Topics:
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {topics.map((topic) => (
                            <Chip
                                key={topic.id}
                                label={topic.name}
                                variant="outlined"
                                color="primary"
                            />
                        ))}
                    </Box>
                </Box>
            )}

            {sources.length > 0 && (
                <Box sx={{ mt: 3 }}>
                    <Typography variant="h6" gutterBottom>
                        Sources:
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {sources.map((source) => (
                            <Chip
                                key={source.id}
                                label={source.name}
                                variant="outlined"
                                color="secondary"
                            />
                        ))}
                    </Box>
                </Box>
            )}

            {techniques.length > 0 && (
                <Box sx={{ mt: 3 }}>
                    <Typography variant="h6" gutterBottom>
                        Techniques:
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {techniques.map((technique) => (
                            <Chip
                                key={technique.id}
                                label={technique.name}
                                variant="outlined"
                                color="success"
                            />
                        ))}
                    </Box>
                </Box>
            )}

            <Box sx={{ mt: 4 }}>
                <Typography variant="h6" gutterBottom>
                    Problem (LaTeX Output):
                </Typography>
                <Box sx={{ p: 2, border: '1px solid #ccc', borderRadius: 2, backgroundColor: '#f9f9f9' }}>
                    <MathRenderer formula={problem.content} />
                </Box>
            </Box>
            <Box sx={{ mt: 4 }}>
                <Typography variant="h6" gutterBottom>
                    Solution (LaTeX Output):
                </Typography>
                <Box sx={{ p: 2, border: '1px solid #ccc', borderRadius: 2, backgroundColor: '#f9f9f9' }}>
                    <MathRenderer formula={problem.solution} />
                </Box>
            </Box>
            <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
                <Button variant="contained" onClick={() => navigate('/')}>
                    Back to Home
                </Button>
            </Box>
        </Container>
    );
}

export default ProblemViewPage;